import {
  SparklesIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import LoadingSpinner from '../../../components/LoadingSpinner';

interface ProcessAllButtonProps {
  onProcess: () => void;
  isProcessing: boolean;
  extractionFailedCount: number;
  generatableCount: number;
  importableCount: number;
  totalReceiptCount: number;
  hasUploadingFiles: boolean;
}

/**
 * Builds a dynamic confirmation message from non-zero action counts.
 * e.g. "Retry 1 extraction, generate 3 bookings, and import 2 bookings?"
 */
const buildConfirmationMessage = (
  extractionFailedCount: number,
  generatableCount: number,
  importableCount: number
): string => {
  const parts: string[] = [];

  if (extractionFailedCount > 0) {
    parts.push(
      `retry ${extractionFailedCount} extraction${extractionFailedCount > 1 ? 's' : ''}`
    );
  }
  if (generatableCount > 0) {
    parts.push(
      `generate ${generatableCount} booking${generatableCount > 1 ? 's' : ''}`
    );
  }
  if (importableCount > 0) {
    parts.push(
      `import ${importableCount} booking${importableCount > 1 ? 's' : ''}`
    );
  }

  if (parts.length === 0) return '';

  // Capitalize first part
  parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);

  if (parts.length === 1) return `${parts[0]}?`;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}?`;
  return `${parts[0]}, ${parts[1]}, and ${parts[2]}?`;
};

const ProcessAllButton: React.FC<ProcessAllButtonProps> = ({
  onProcess,
  isProcessing,
  extractionFailedCount,
  generatableCount,
  importableCount,
  totalReceiptCount,
  hasUploadingFiles,
}) => {
  const processableCount =
    extractionFailedCount + generatableCount + importableCount;
  const isDisabled =
    processableCount === 0 || hasUploadingFiles || isProcessing;

  let disabledReason = '';
  if (totalReceiptCount === 0) {
    disabledReason = 'Add receipts to get started.';
  } else if (hasUploadingFiles) {
    disabledReason = 'Please wait for uploads to complete.';
  } else if (processableCount === 0 && !isProcessing) {
    disabledReason = 'All receipts have been processed!';
  }

  const confirmationMessage = buildConfirmationMessage(
    extractionFailedCount,
    generatableCount,
    importableCount
  );

  return (
    <div className='flex items-end flex-col'>
      <ConfirmableButton
        onConfirm={onProcess}
        buttonClassName='flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium shadow-md hover:shadow-lg'
        confirmationMessage={confirmationMessage}
        disabled={isDisabled}
      >
        {isProcessing ? (
          <>
            <LoadingSpinner size='sm' className='mr-3' />
            Processing...
          </>
        ) : (
          <>
            <SparklesIcon className='w-5 h-5 mr-2' />
            Process All
          </>
        )}
      </ConfirmableButton>
      {isDisabled && disabledReason && !isProcessing && (
        <div className='mt-2 flex items-center italic gap-1 text-sm text-gray-600 dark:text-gray-400'>
          <InformationCircleIcon className='w-5 h-5 flex-shrink-0 mt-0.5' />
          <p>{disabledReason}</p>
        </div>
      )}
    </div>
  );
};

export default ProcessAllButton;
